<template>
  <!-- Background Image/Video -->
  <div class="hero-homepage__background">
    <video
      v-if="data.video && !videoError"
      ref="backgroundVideo"
      class="hero-homepage__background__video"
      autoplay
      muted
      loop
      playsinline
      @error="handleVideoError"
    >
      <source
        :src="data.video"
        type="video/mp4"
      >
    </video>
    <img
      v-else
      :src="data.video_fallback_image || data.background_image"
      :alt="data.title || 'Hero background'"
      class="hero-homepage__background__image"
    >
    <!-- Dark Overlay -->
    <div class="hero-homepage__background__overlay" />
  </div>

  <div class="container">
    <!-- Main Content -->
    <div
      v-if="data.title || data.description || data.button_text"
      class="hero-homepage__content"
    >
      <h1
        v-if="data.title"
        class="hero-homepage__content__title"
      >
        {{ data.title }}
      </h1>
      <p
        v-if="data.description"
        class="hero-homepage__content__description"
      >
        {{ data.description }}
      </p>
      <a
        v-if="data.button_text && data.button_link"
        :href="data.button_link"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Learn more (opens in new tab)"
        class="btn-custom primary action-link"
      >
        {{ data.button_text }}
      </a>
    </div>

    <!-- Partner Logos -->
    <div
      v-if="data.brands_id"
      class="hero-homepage__brands"
    >
      <!-- Loading State -->
      <div
        v-if="loading"
        class="hero-homepage__brands__loading"
      >
        <div class="loading-spinner" />
      </div>

      <!-- Error State -->
      <div
        v-if="error"
        class="hero-homepage__brands__error"
      >
        <p>{{ error }}</p>
      </div>

      <!-- Brands Slider -->
      <div
        v-if="!loading && !error && brands.length > 0"
        ref="splideContainer"
        class="splide hero-homepage__brands__slider"
      >
        <div class="splide__track">
          <ul class="splide__list">
            <li
              v-for="brand in brands"
              :key="brand.brand_id"
              class="splide__slide hero-homepage__brands__slider__slide"
            >
              <a
                :href="`/searchbrand?querybrand=${brand.brand_id}&name=${
                  encodeURIComponent(brand.name)
                }`"
                :title="brand.name"
                class="hero-homepage__brands__slider__slide__item"
              >
                <img
                  :src="brand.image_path"
                  :alt="brand.name"
                  loading="lazy"
                >
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Splide from '@splidejs/splide';

export default {
  name: 'HeroHomepage',
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      brands: [],
      loading: false,
      error: null,
      splideInstance: null,
      videoError: false,
    };
  },

  mounted() {
    if (this.data.brands_id) {
      this.fetchBrands();
    }
  },

  beforeUnmount() {
    if (this.splideInstance) {
      this.splideInstance.destroy();
    }
  },

  methods: {
    handleVideoError() {
      this.videoError = true;
    },

    fetchBrands() {
      if (!this.data.brands_id) return;

      this.loading = true;
      this.error = null;

      $.ajax({
        type: 'post',
        url: '/ajax/merchandising',
        headers: {
          'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
        },
        data: {
          groupBrandMerchandising_ids: [this.data.brands_id],
        },
        timeout: 30000,
        success: (response) => {
          this.loading = false;
          this.brands = response.data?.brandmerchandising?.[this.data.brands_id] || [];
          this.$nextTick(() => this.initSplide());
        },
        error: (xhr, status, error) => {
          this.loading = false;
          this.error = `Error loading brands: ${status} - ${error}`;
          this.brands = [];
        },
      });
    },

    initSplide() {
      if (this.splideInstance) {
        this.splideInstance.destroy();
      }

      const slideCount = this.brands.length;
      if (this.brands.length === 0 || !this.$refs.splideContainer) return;

      const config = {
        type: 'loop',
        rewind: true,
        perPage: 7,
        perMove: 7,
        gap: 15,
        pagination: false,
        drag: slideCount > 7,
        arrows: slideCount > 7,
        height: 90,
        autoplay: slideCount > 7,
        interval: 5000,
        pauseOnHover: true,
        pauseOnFocus: true,
        pauseOnScroll: true,
        pauseOnClick: true,
        pauseOnTouch: true,

        breakpoints: {
          1469: {
            perPage: 6,
            drag: slideCount > 6,
            autoplay: slideCount > 6,
            arrows: slideCount > 6,
            perMove: 6,
          },
          1200: {
            perPage: 5,
            drag: slideCount > 5,
            autoplay: slideCount > 5,
            arrows: slideCount > 5,
            perMove: 5,
          },
          900: {
            perPage: 4,
            drag: slideCount > 4,
            autoplay: slideCount > 4,
            arrows: slideCount > 4,
            perMove: 4,
          },
          768: {
            perPage: 3,
            drag: slideCount > 3,
            autoplay: slideCount > 3,
            arrows: slideCount > 3,
            perMove: 3,
          },
          600: {
            perPage: 2,
            drag: slideCount > 2,
            autoplay: slideCount > 2,
            arrows: slideCount > 2,
            perMove: 2,
          },

        },
      };

      this.splideInstance = new Splide(this.$refs.splideContainer, config);
      this.splideInstance.mount();
    },
  },
};
</script>
